import React, { useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from 'lucide-react';
import { Account, AccountCreate, AccountUpdate, AccountGroupResponse } from '@/app/types';

// Zod Schema for Account Form
const formSchema = z.object({
  name: z.string().min(1, 'Account name is required').max(100),
  code: z.string().max(50)
    .refine(s => !s || !/\s/.test(s), 'Code cannot contain spaces')
    .optional()
    .or(z.literal('')),
  account_group_id: z.number({ required_error: "Account group is required" }),
  description: z.string().optional().nullable(),
  is_active: z.boolean().default(true),
});

type AccountFormValues = z.infer<typeof formSchema>;

interface AccountFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: AccountCreate | AccountUpdate) => void;
  defaultValues?: Partial<Account>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
  accountGroups: AccountGroupResponse[];
  lockedAccountGroupId?: number; // When set, the account group is pre-selected and cannot be changed
}

const AccountForm: React.FC<AccountFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
  accountGroups,
  lockedAccountGroupId,
}) => {
  // Find the locked group name for display
  const lockedGroup = lockedAccountGroupId
    ? accountGroups.find(g => g.id === lockedAccountGroupId)
    : null;
  // Initialize the form with defaultValues or empty values
  const form = useForm<AccountFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      code: '',
      account_group_id: 0,
      description: '',
      is_active: true,
    },
  });

  // Reset form when dialog opens with new data
  useEffect(() => {
    if (open) {
      if (defaultValues) {
        form.reset({
          name: defaultValues.name || '',
          code: defaultValues.code || '',
          account_group_id: lockedAccountGroupId || defaultValues.account_group_id || 0,
          description: defaultValues.description || '',
          is_active: defaultValues.is_active !== undefined ? defaultValues.is_active : true,
        });
      } else {
        form.reset({
          name: '',
          code: '',
          account_group_id: lockedAccountGroupId || 0,
          description: '',
          is_active: true,
        });
      }
    }
  }, [open, defaultValues, form, lockedAccountGroupId]);

  const handleSubmit: SubmitHandler<AccountFormValues> = (values: AccountFormValues): void => {
    onSubmit(values);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create New Account' : 'Edit Account'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Name</FormLabel>
                    <FormControl>
                      <Input {...field} autoFocus />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Account Code</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value || ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="account_group_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Account Group</FormLabel>
                  {lockedAccountGroupId ? (
                    <FormControl>
                      <Input
                        value={lockedGroup ? `${lockedGroup.name}${lockedGroup.code ? ` (${lockedGroup.code})` : ''}` : ''}
                        disabled
                        className="bg-muted"
                      />
                    </FormControl>
                  ) : (
                    <Select
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      value={field.value ? field.value.toString() : undefined}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select Account Group" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {accountGroups.map((group) => (
                          <SelectItem key={group.id} value={group.id.toString()}>
                            {group.name} ({group.code})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      value={field.value || ''} 
                      placeholder="Account description (optional)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4 mt-6">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Active</FormLabel>
                    <FormDescription>
                      Inactive accounts won&apos;t appear in dropdown menus for new transactions.
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button 
                type="button"
                variant="secondary"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AccountForm; 